import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { BudgetPhases } from 'business/constants';
import { useTranslation } from 'react-i18next';
import { FUNCTION_BUDGET_PHASE_COLORS } from 'routes/BudgetModule/config/constants';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpError, RQAPICall } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import Button from '@mui/material/Button';
import ModalTemplate from 'ui/ModalTemplate/ModalTemplate';
import { Box, TextField, Typography } from '@mui/material';
import { GridApi } from 'ag-grid-community';
import { refreshBudgetProposalRow } from '../CanResourceEditCellRenderer/gridHelpers';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type Props = {
    phase: BudgetPhases;
    bpName: string;
    bpId: number;
    gridApi: GridApi;
    direction: 'next' | 'previous';
};

const StyHighlight = styled(Box, {
    name: 'StyHighlight',
})(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.primary.main,
}));

const StyMovePhaseButton = styled(Button, {
    name: 'StyMovePhaseButton',
    shouldForwardProp: (prop) => prop !== 'phase',
})<{ phase: Props['phase'] }>(({ phase }) => ({
    height: 'max-content',
    width: 'max-content',
    borderRadius: 20,
    padding: '0.4rem 0.8rem',
    fontSize: '1em',
    fontWeight: 'normal',
    minHeight: 'max-content',
    color: FUNCTION_BUDGET_PHASE_COLORS.color[phase],
    '&:hover': {
        backgroundColor: FUNCTION_BUDGET_PHASE_COLORS.background[phase],
    },
}));

const updateLoadingBudgetProposal = (
    gridApi: GridApi,
    bpId: number,
    isLoading?: boolean,
    phase?: BudgetPhases,
) => {
    refreshBudgetProposalRow(gridApi, bpId, (bp) => {
        const bpCopy = { ...bp };
        if (isLoading !== undefined) bpCopy.isLoading = isLoading;
        if (phase) {
            bpCopy.phase = phase;
            bpCopy.canResourceEdit = [];
        }
        return bpCopy;
    });
};

function MoveBudgetPhase({
    bpId,
    bpName,
    phase,
    gridApi,
    direction,
}: Props): React.ReactElement {
    const [showConfirmPhase, setShowConfirmPhase] = useState(false);
    const toggleConfirmPhase = () => {
        setShowConfirmPhase(!showConfirmPhase);
    };
    const [isValid, setIsValid] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    const confirm = t('common.confirm').toLowerCase();
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const keywordMatches = value.toLowerCase().trim() === confirm;
        setIsValid(keywordMatches);
    };
    const updatePhaseMutation = useMutation({
        mutationFn: async () => {
            const response = await RQAPICall({
                endpoint: `budget_proposals/move_to_phase`,
                method: 'POST',
                body: { id: bpId, direction },
            });
            return response;
        },

        onSuccess: () => {
            updateLoadingBudgetProposal(gridApi, bpId, false, phase);
            // Invalidate the cache to refetch the data. If we use resetQueries instead, there will be
            // a rerender where the result of this query will be empty, which causes an issue with AgGrid
            // autosize columns in ManageBudgetTable
            queryClient.invalidateQueries({
                queryKey: budgetProposalsKeys({ budgetProposalId: bpId }),
            });

            // TODO / DEBT: See comment at:
            // src/routes/Admin/Budget/BudgetManagement/AddBudget/hooks.ts
            queryClient.invalidateQueries({
                queryKey: [budgetProposalsKeys()[0]],
            });
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
            updateLoadingBudgetProposal(gridApi, bpId, false);
        },
    });

    const closeAndSubmitPhase = () => {
        updateLoadingBudgetProposal(gridApi, bpId, true);
        toggleConfirmPhase();
        updatePhaseMutation.mutate();
    };
    const confirmationSentence = (
        <Typography sx={{ whiteSpace: 'pre-line' }}>
            {t('budgetProposal.confirmPhasePart1', { context: direction })}{' '}
            <StyHighlight component="span">{bpName}</StyHighlight>{' '}
            {t('budgetProposal.confirmPhasePart2')}{' '}
            <StyHighlight component="span">
                {t(`budgetProposal.${phase}`)}
            </StyHighlight>{' '}
            {t('budgetProposal.confirmPhasePart3')}{' '}
            {direction === 'previous' && t('budgetProposal.confirmPhasePart4')}
        </Typography>
    );
    const confirmationExplanation = (
        <Typography sx={{ mt: 2 }}>
            {t('budgetProposal.confirmationWordPart1')}{' '}
            <StyHighlight component="span">{confirm}</StyHighlight>{' '}
            {t('budgetProposal.confirmationWordPart2')}
        </Typography>
    );
    const label = `${t('budgetProposal.moveTo', { context: direction })} ${t(
        `budgetProposal.${phase}`,
    )}`;

    // Focuses the input field when the modal opens
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (showConfirmPhase) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [showConfirmPhase]);

    return (
        <>
            <StyMovePhaseButton phase={phase} onClick={toggleConfirmPhase}>
                {direction === 'previous' && (
                    <ArrowBackIosIcon sx={{ height: '1rem' }} />
                )}
                {label}
                {direction === 'next' && (
                    <ArrowForwardIosIcon sx={{ height: '1rem' }} />
                )}
            </StyMovePhaseButton>
            <ModalTemplate
                openModal={showConfirmPhase}
                onCloseModal={toggleConfirmPhase}
                onAction={closeAndSubmitPhase}
                actionText={t('budgetProposal.confirmButtonMovePhase', {
                    context: direction,
                })}
                modalTitle={t('budgetProposal.confirmPhaseTitle')}
                disableActionButton={!isValid}
            >
                {confirmationSentence}
                {confirmationExplanation}
                <TextField
                    placeholder={confirm}
                    onChange={onChange}
                    size="small"
                    error={!isValid}
                    sx={{ mt: 2 }}
                    inputRef={inputRef}
                />
            </ModalTemplate>
        </>
    );
}

export default MoveBudgetPhase;
